import { z } from 'zod';
import { encodeBody } from '../http/encode.js';
import { availabilitySchema, countEntriesSchema, countSessionCreateSchema, countSessionSchema, countSubmitSchema, countSummarySchema, manifestVerificationSchema, releasedCountSchema, reorderRuleSchema, reorderRuleUpsertSchema, replenishmentRunCreateSchema, replenishmentRunSchema, requisitionSchema, reservationCreateSchema, reservationReleaseSchema, reservationSchema, suggestionConvertSchema, suggestionSchema, } from '../schemas/planning.js';
const reservationListSchema = z.array(reservationSchema);
const countSessionListSchema = z.array(countSessionSchema);
const reorderRuleListSchema = z.array(reorderRuleSchema);
const runListSchema = z.array(replenishmentRunSchema);
const suggestionListSchema = z.array(suggestionSchema);
export class ReservationsApi {
    transport;
    constructor(transport) {
        this.transport = transport;
    }
    /**
     * On hand, held hard, held soft, issuable and available-to-promise.
     *
     * A read that writes nothing — the first draft of this endpoint locked the balance row
     * and swept expiries, which meant an enquiry mutated the database and, against another
     * tenant's item, tried to create a balance row for an item that tenant does not have.
     * Safe to call on every keystroke of a picker screen.
     */
    availability(params, signal) {
        return this.transport.request({
            method: 'GET',
            path: '/v1/stock/availability',
            query: {
                item_id: params.item_id,
                warehouse_id: params.warehouse_id,
                stock_state: params.stock_state ?? 'AVAILABLE',
            },
            schema: availabilitySchema,
            signal,
        });
    }
    list(filter = {}, signal) {
        return this.transport.request({
            method: 'GET',
            path: '/v1/reservations',
            query: {
                item_id: filter.item_id,
                warehouse_id: filter.warehouse_id,
                reservation_status: filter.reservation_status,
                reference_type: filter.reference_type,
                reference_id: filter.reference_id,
            },
            schema: reservationListSchema,
            signal,
        });
    }
    get(reservationId, signal) {
        return this.transport.request({
            method: 'GET',
            path: `/v1/reservations/${encodeURIComponent(reservationId)}`,
            schema: reservationSchema,
            signal,
        });
    }
    create(payload, operation, signal) {
        return this.transport.request({
            method: 'POST',
            path: '/v1/reservations',
            body: encodeBody(reservationCreateSchema, payload, 'createReservation'),
            operation,
            schema: reservationSchema,
            signal,
        });
    }
    release(reservationId, payload, operation, signal) {
        return this.transport.request({
            method: 'POST',
            path: `/v1/reservations/${encodeURIComponent(reservationId)}/release`,
            body: encodeBody(reservationReleaseSchema, payload, 'releaseReservation'),
            operation,
            schema: reservationSchema,
            signal,
        });
    }
    /**
     * Housekeeping only.
     *
     * Expiry is already exact on the posting path — lapsed holds are swept inside the
     * posting lock before availability is evaluated, so an issue one second after a hold
     * lapses succeeds with no sweeper having run. This endpoint exists for warehouses
     * nothing is being issued from, where lapsed holds would otherwise accumulate in the
     * projection. A skin should not present it as something a person has to remember.
     */
    releaseExpired(warehouseId, operation, signal) {
        return this.transport
            .request({
            method: 'POST',
            path: '/v1/reservations/release-expired',
            query: { warehouse_id: warehouseId },
            operation,
            schema: releasedCountSchema,
            signal,
        })
            .then((r) => r.released);
    }
}
export class CountingApi {
    transport;
    constructor(transport) {
        this.transport = transport;
    }
    list(filter = {}, signal) {
        return this.transport.request({
            method: 'GET',
            path: '/v1/count-sessions',
            query: { warehouse_id: filter.warehouse_id, count_status: filter.count_status },
            schema: countSessionListSchema,
            signal,
        });
    }
    get(sessionId, signal) {
        return this.transport.request({
            method: 'GET',
            path: `/v1/count-sessions/${encodeURIComponent(sessionId)}`,
            schema: countSessionSchema,
            signal,
        });
    }
    create(payload, operation, signal) {
        return this.transport.request({
            method: 'POST',
            path: '/v1/count-sessions',
            body: encodeBody(countSessionCreateSchema, payload, 'createCountSession'),
            operation,
            schema: countSessionSchema,
            signal,
        });
    }
    /** Snapshot the expected quantity on every position in scope, once. */
    freeze(sessionId, operation, signal) {
        return this.transport.request({
            method: 'POST',
            path: `/v1/count-sessions/${encodeURIComponent(sessionId)}/freeze`,
            operation,
            schema: countSessionSchema,
            signal,
        });
    }
    recordEntries(sessionId, payload, operation, signal) {
        return this.transport.request({
            method: 'POST',
            path: `/v1/count-sessions/${encodeURIComponent(sessionId)}/entries`,
            body: encodeBody(countEntriesSchema, payload, 'recordCount'),
            operation,
            schema: countSessionSchema,
            signal,
        });
    }
    /**
     * Seal the count and raise the approval. A count with no variance needs none.
     *
     * Approval posts a DELTA adjustment, never a set-to-value (D-32): freeze at 100, count
     * 98, let an issue of 10 land in between, approve — the balance is 88 and there are two
     * distinct movements. Any implementation that writes the counted figure onto the balance
     * silently destroys the issue.
     */
    submit(sessionId, payload, operation, signal) {
        return this.transport.request({
            method: 'POST',
            path: `/v1/count-sessions/${encodeURIComponent(sessionId)}/submit`,
            body: encodeBody(countSubmitSchema, payload, 'submitCountSession'),
            operation,
            schema: countSessionSchema,
            signal,
        });
    }
    cancel(sessionId, operation, signal) {
        return this.transport.request({
            method: 'POST',
            path: `/v1/count-sessions/${encodeURIComponent(sessionId)}/cancel`,
            operation,
            schema: countSessionSchema,
            signal,
        });
    }
    summary(sessionId, signal) {
        return this.transport.request({
            method: 'GET',
            path: `/v1/count-sessions/${encodeURIComponent(sessionId)}/summary`,
            schema: countSummarySchema,
            signal,
        });
    }
    verifyManifest(sessionId, signal) {
        return this.transport.request({
            method: 'GET',
            path: `/v1/count-sessions/${encodeURIComponent(sessionId)}/verify-manifest`,
            schema: manifestVerificationSchema,
            signal,
        });
    }
}
export class ReplenishmentApi {
    transport;
    constructor(transport) {
        this.transport = transport;
    }
    listRules(warehouseId, signal) {
        return this.transport.request({
            method: 'GET',
            path: '/v1/reorder-rules',
            query: { warehouse_id: warehouseId },
            schema: reorderRuleListSchema,
            signal,
        });
    }
    getRule(params, signal) {
        return this.transport.request({
            method: 'GET',
            path: '/v1/reorder-rules/for',
            query: { item_id: params.item_id, warehouse_id: params.warehouse_id },
            schema: reorderRuleSchema,
            signal,
        });
    }
    /**
     * The reorder comparison the whole estate disagreed about is settled here as
     * `on_hand <= reorder_point`, guarded by `reorder_point > 0`. Assets used `<=`, Accounts
     * used `<` in its report and `<=` on its items page, so an item sitting exactly on its
     * level was flagged in one place and not the other.
     */
    upsertRule(payload, operation, signal) {
        return this.transport.request({
            method: 'PUT',
            path: '/v1/reorder-rules',
            body: encodeBody(reorderRuleUpsertSchema, payload, 'upsertReorderRule'),
            operation,
            schema: reorderRuleSchema,
            signal,
        });
    }
    deleteRule(ruleId, operation, signal) {
        return this.transport.request({
            method: 'DELETE',
            path: `/v1/reorder-rules/${encodeURIComponent(ruleId)}`,
            operation,
            signal,
        });
    }
    listRuns(warehouseId, signal) {
        return this.transport.request({
            method: 'GET',
            path: '/v1/replenishment-runs',
            query: { warehouse_id: warehouseId },
            schema: runListSchema,
            signal,
        });
    }
    getRun(runId, signal) {
        return this.transport.request({
            method: 'GET',
            path: `/v1/replenishment-runs/${encodeURIComponent(runId)}`,
            schema: replenishmentRunSchema,
            signal,
        });
    }
    /** The run produces suggestions. It never orders (D-34). */
    run(payload, operation, signal) {
        return this.transport.request({
            method: 'POST',
            path: '/v1/replenishment-runs',
            body: encodeBody(replenishmentRunCreateSchema, payload, 'runReplenishment'),
            operation,
            schema: replenishmentRunSchema,
            signal,
        });
    }
    listSuggestions(filter = {}, signal) {
        return this.transport.request({
            method: 'GET',
            path: '/v1/replenishment-suggestions',
            query: { run_id: filter.run_id, suggestion_status: filter.suggestion_status },
            schema: suggestionListSchema,
            signal,
        });
    }
    /** A person converts suggestions into a requisition. */
    convertSuggestions(payload, operation, signal) {
        return this.transport.request({
            method: 'POST',
            path: '/v1/replenishment-suggestions/convert',
            body: encodeBody(suggestionConvertSchema, payload, 'convertSuggestions'),
            operation,
            schema: requisitionSchema,
            signal,
        });
    }
    dismissSuggestion(suggestionId, operation, signal) {
        return this.transport.request({
            method: 'POST',
            path: `/v1/replenishment-suggestions/${encodeURIComponent(suggestionId)}/dismiss`,
            operation,
            schema: suggestionSchema,
            signal,
        });
    }
}
//# sourceMappingURL=planning.js.map